/**
 * 前后端共用的 Zod Schema
 * - 同时供前端表单校验与后端 API 校验
 *
 * 重构版(2026-06 AB 队方案):
 * - A 队 = 小物归队(校园失物寻回)
 * - B 队 = 叶子在说话(植物成长陪伴)
 * 旧 observe/reason/evaluate/record schema 保留以兼容旧前端
 */
import { z } from 'zod';

// ============================================================
// A 队 · 小物归队
// ============================================================

const dataUrlString = z
  .string()
  .refine((s) => s.startsWith('data:image/'), { message: '需要图片 data URL' });

export const A_CATEGORIES = [
  '水杯',
  '校卡',
  '文具',
  '书本资料',
  '书包',
  '衣物',
  '鞋帽',
  '雨伞',
  '钥匙',
  '眼镜',
  '手表',
  '电子设备',
  '耳机',
  '饭盒餐具',
  '体育用品',
  '玩具',
  '钱包零钱包',
  '红领巾队徽',
  '其他',
] as const;
export type ACategory = (typeof A_CATEGORIES)[number];

export const A_SEARCH_MODES = ['text', 'voice', 'image'] as const;
export type ASearchMode = (typeof A_SEARCH_MODES)[number];

/**
 * 状态机(信任型自助领取)
 *  available  等待主人寻找
 *  returned   已被主人取回
 *  expired    长期无人领取(可选)
 */
export const A_STATUSES = ['available', 'returned', 'expired'] as const;
export type AItemStatus = (typeof A_STATUSES)[number];

/** 公开墙筛选分组 */
export const A_FILTER_GROUPS = [
  { value: 'all', label: '全部' },
  { value: 'available', label: '等待寻找' },
  { value: 'returned', label: '已找回' },
] as const;
export type AFilterGroup = (typeof A_FILTER_GROUPS)[number]['value'];

/** 隐私检查状态 */
export const A_PRIVACY_STATUSES = ['safe', 'pending', 'blocked'] as const;
export type APrivacyStatus = (typeof A_PRIVACY_STATUSES)[number];

/** 1) 失物发布 */
export const AnalyzePublishItemRequestSchema = z.object({
  photoDataUrl: dataUrlString,
  category: z.enum(A_CATEGORIES),
  foundLocation: z.string().max(40).optional().default(''),
  foundTime: z.string().max(40).optional().default(''),
});
export type AnalyzePublishItemRequest = z.infer<typeof AnalyzePublishItemRequestSchema>;

export const PublishItemRequestSchema = z.object({
  photoDataUrl: dataUrlString.optional().nullable(),
  category: z.enum(A_CATEGORIES),
  title: z.string().min(1, '请填写物品名称').max(40),
  description: z.string().max(200).optional().default(''),
  foundLocation: z.string().max(40).optional().default(''),
  storageLocation: z.string().min(1, '请填写固定存放地点').max(40),
  /** 学生在 AI 结果后手动确认/修改的特征数组 */
  aiFeaturesConfirmed: z.array(z.string()).optional().default([]),
});
export type PublishItemRequest = z.infer<typeof PublishItemRequestSchema>;

export const ItemFeaturesSchema = z.object({
  color: z.string().optional(),
  shape: z.string().optional(),
  text_mark: z.string().optional(),
  pattern: z.string().optional(),
  size: z.string().optional(),
  notes: z.array(z.string()).optional(),
  privacyWarning: z.string().nullable().optional().default(null),
  privacyCheckStatus: z.enum(A_PRIVACY_STATUSES).optional().default('safe'),
});
export type ItemFeatures = z.infer<typeof ItemFeaturesSchema>;

export const PublishItemResultSchema = z.object({
  itemId: z.string(),
  features: ItemFeaturesSchema,
  privacyWarning: z.string().nullable(),
  privacyCheckStatus: z.enum(A_PRIVACY_STATUSES),
  /** 是否允许发布(privacyCheckStatus === 'blocked' 时为 false) */
  canPublish: z.boolean(),
  blockedReasons: z.array(z.string()),
});
export type PublishItemResult = z.infer<typeof PublishItemResultSchema>;

/** 2) 失物详情(公开版) */
export const LostItemSchema = z.object({
  id: z.string(),
  category: z.enum(A_CATEGORIES),
  title: z.string(),
  description: z.string().optional().default(''),
  foundLocation: z.string().optional().default(''),
  storageLocation: z.string().optional().default(''),
  imageUrl: z.string().nullable(),
  imageKey: z.string().nullable().optional(),
  features: ItemFeaturesSchema,
  privacyFlag: z.boolean(),
  status: z.enum(A_STATUSES),
  privacyCheckStatus: z.enum(A_PRIVACY_STATUSES).default('safe'),
  publishedAt: z.number(),
  returnedAt: z.number().nullable(),
});
export type LostItem = z.infer<typeof LostItemSchema>;

/** 3) 智能寻找 */
export const SearchItemRequestSchema = z
  .object({
    mode: z.enum(A_SEARCH_MODES),
    queryText: z.string().max(300).optional(),
    queryVoiceDataUrl: dataUrlString.optional(),
    queryImageDataUrl: dataUrlString.optional(),
    lastSeenLocation: z.string().max(40).optional().default(''),
    lastSeenTime: z.string().max(40).optional().default(''),
  })
  .refine(
    (v) =>
      (v.mode === 'text' && !!v.queryText) ||
      (v.mode === 'voice' && !!v.queryVoiceDataUrl) ||
      (v.mode === 'image' && !!v.queryImageDataUrl),
    { message: '请填写与模式对应的输入' },
  );
export type SearchItemRequest = z.infer<typeof SearchItemRequestSchema>;

export const CandidateSchema = z.object({
  item: LostItemSchema,
  score: z.number().min(0).max(1),
  matchedFeatures: z.array(z.string()),
  difference: z.string().optional().default(''),
});
export type Candidate = z.infer<typeof CandidateSchema>;

export const SearchItemResultSchema = z.object({
  searchId: z.string(),
  candidates: z.array(CandidateSchema).max(3),
  uncertain: z.string().nullable(),
  /** AI 主动追问项(当多候选无法判定时) */
  followUpQuestions: z.array(z.string()).default([]),
  /** 是否需要补充细节(多候选相似) */
  needsMoreDetail: z.boolean().default(false),
});
export type SearchItemResult = z.infer<typeof SearchItemResultSchema>;

/** 自助领取反馈(可选) */
export const SearchFeedbackSchema = z.object({
  searchId: z.string(),
  selectedItemId: z.string(),
  feedback: z.enum(['found', 'not_found', 'not_mine']),
});
export type SearchFeedback = z.infer<typeof SearchFeedbackSchema>;

/** 4) 验证归还 -> 简化为"我已取回" 反馈 */
export const ClaimAnswerRequestSchema = z.object({
  itemId: z.string(),
  /** 二次确认:是否真的已现场取回 */
  confirmed: z.literal(true),
});
export type ClaimAnswerRequest = z.infer<typeof ClaimAnswerRequestSchema>;

/** 5) 统计与防丢报告(2.0) */
export const StatsReportSchema = z.object({
  // 状态分布
  total: z.number(),
  available: z.number(),
  returned: z.number(),
  expired: z.number().default(0),
  returnRate: z.number(),
  byCategory: z.array(z.object({ category: z.enum(A_CATEGORIES), count: z.number() })),
  byFoundLocation: z.array(z.object({ location: z.string(), count: z.number() })).max(10),
  // AI 表现
  aiBuildTestCorrect: z.number(),
  textSearchTop3Hit: z.number(),
  voiceSearchTop3Hit: z.number(),
  imageSearchTop3Hit: z.number(),
  foundCount: z.number(),
  notFoundCount: z.number(),
  notMineCount: z.number(),
  // 防丢建议
  advice: z.array(z.string()),
});
export type StatsReport = z.infer<typeof StatsReportSchema>;

// ============================================================
// B 队 · 叶子在说话
// ============================================================

export const B_LEAF_STATES = ['正常', '下垂', '发黄', '卷曲', '有损伤', '长新叶'] as const;
export type BLeafState = (typeof B_LEAF_STATES)[number];

export const B_SOIL_STATES = ['偏干', '微湿', '很湿'] as const;
export type BSoilState = (typeof B_SOIL_STATES)[number];

export const B_LIGHT_POSITIONS = [
  '窗边明亮处',
  '室内明亮处',
  '较暗位置',
  '可能暴晒',
] as const;
export type BLightPosition = (typeof B_LIGHT_POSITIONS)[number];

export const B_WATER_ACTIONS = ['未浇水', '少量浇水', '正常浇水'] as const;
export type BWaterAction = (typeof B_WATER_ACTIONS)[number];

export const B_CARE_ACTIONS = ['浇水', '调整光照', '继续观察', '补充观察', '完成今日任务'] as const;
export type BCareAction = (typeof B_CARE_ACTIONS)[number];

/** 1) 建档 */
export const CreatePlantRequestSchema = z.object({
  name: z.string().min(1, '请为植物起个名字').max(20),
  species: z.string().default('绿萝'),
  location: z.string().min(1, '请填写摆放位置').max(40),
  firstPhotoDataUrl: dataUrlString.optional().nullable(),
});
export type CreatePlantRequest = z.infer<typeof CreatePlantRequestSchema>;

export const PlantSchema = z.object({
  id: z.string(),
  name: z.string(),
  species: z.string(),
  location: z.string(),
  firstPhotoUrl: z.string().nullable(),
  firstPhotoKey: z.string().nullable().optional(),
  createdAt: z.number(),
});
export type Plant = z.infer<typeof PlantSchema>;

/** 拍照记录时 AI 识别快照(种类/健康/虫害) */
export const AiObservationSnapshotSchema = z.object({
  species: z.string().optional(),
  confidence: z.enum(['high', 'medium', 'low']).optional(),
  healthStatus: z.enum(['健康', '亚健康', '病态']).optional(),
  hasPestIssue: z.boolean().optional(),
  pestSummary: z.string().optional(),
  pests: z
    .array(
      z.object({
        name: z.string(),
        severity: z.string(),
        treatment: z.string().optional(),
      }),
    )
    .optional(),
  overallAssessment: z.string().optional(),
});
export type AiObservationSnapshot = z.infer<typeof AiObservationSnapshotSchema>;

/** 2) 每日观察 */
export const DailyObservationRequestSchema = z.object({
  plantId: z.string(),
  /** 可选;不传时后端按该植物已有记录自动 +1 */
  day: z.number().int().min(1).max(60).optional(),
  photoDataUrl: dataUrlString,
  soil: z.enum(B_SOIL_STATES).optional(),
  lastWatering: z.string().max(20).optional().default(''),
  lightPosition: z.enum(B_LIGHT_POSITIONS).optional(),
  leafState: z.enum(B_LEAF_STATES).optional(),
  growthChange: z.string().max(500).optional().default(''),
  wateredToday: z.enum(B_WATER_ACTIONS).optional(),
  note: z.string().max(200).optional().default(''),
  aiSnapshot: AiObservationSnapshotSchema.optional(),
});
export type DailyObservationRequest = z.infer<typeof DailyObservationRequestSchema>;

export const SpeciesMismatchSchema = z.object({
  detected: z.string(),
  expected: z.string(),
});
export type SpeciesMismatch = z.infer<typeof SpeciesMismatchSchema>;

export const ObservationResultSchema = z.object({
  observationId: z.string(),
  /** 该植物第几次拍照记录(历史字段名 day) */
  day: z.number(),
  recordIndex: z.number(),
  createdAt: z.number(),
  visibleObservations: z.array(z.string()),
  abnormalLevel: z.enum(['正常', '轻度', '明显']),
  followUpQuestions: z.array(z.string()),
  aiSnapshot: AiObservationSnapshotSchema.nullable().optional(),
  speciesMismatch: SpeciesMismatchSchema.nullable().optional(),
});
export type ObservationResult = z.infer<typeof ObservationResultSchema>;

/** 诊断页用的观察上下文(含植物信息) */
export const ObservationContextSchema = z.object({
  observationId: z.string(),
  plantId: z.string(),
  /** @deprecated 使用 recordIndex */
  day: z.number(),
  recordIndex: z.number(),
  photoUrl: z.string().nullable(),
  photoKey: z.string().nullable().optional(),
  soil: z.string().nullable(),
  lastWatering: z.string().nullable(),
  lightPosition: z.string().nullable(),
  leafState: z.string().nullable(),
  growthChange: z.string().nullable(),
  wateredToday: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.number(),
  aiSnapshot: AiObservationSnapshotSchema.nullable().optional(),
  plant: z.object({
    id: z.string(),
    name: z.string(),
    species: z.string(),
    location: z.string(),
  }),
});
export type ObservationContext = z.infer<typeof ObservationContextSchema>;

/** 3) AI 今日建议(严格匹配 doc 265 行的 JSON) */
export const CareAdviceResultSchema = z.object({
  adviceId: z.string(),
  plant_name: z.string(),
  today_observation: z.array(z.string()),
  soil_status: z.enum(B_SOIL_STATES),
  last_watered: z.string(),
  light_position: z.enum(B_LIGHT_POSITIONS),
  today_action: z.string(),
  reminder: z.string(),
  uncertainty: z.string(),
  fallback: z.boolean().optional(),
  /** 诊断保存时间(毫秒),读取历史诊断时有值 */
  diagnosedAt: z.number().optional(),
});
export type CareAdviceResult = z.infer<typeof CareAdviceResultSchema>;

export const CareActionRecordSchema = z.object({
  action: z.string(),
  performedAt: z.number().nullable(),
  createdAt: z.number(),
});
export type CareActionRecord = z.infer<typeof CareActionRecordSchema>;

/** 4) 护理动作(用户确认/补充观察) */
export const CareActionRequestSchema = z.object({
  plantId: z.string(),
  adviceId: z.string().optional(),
  observationId: z.string().optional(),
  action: z.enum(B_CARE_ACTIONS),
  performed: z.boolean(),
  note: z.string().max(200).optional().default(''),
});
export type CareActionRequest = z.infer<typeof CareActionRequestSchema>;

/** 5) 成长时间线 */
export const TimelineDaySchema = z.object({
  /** @deprecated 使用 recordIndex;与 recordIndex 相同 */
  day: z.number(),
  recordIndex: z.number(),
  observationId: z.string().nullable(),
  photoUrl: z.string().nullable(),
  photoKey: z.string().nullable().optional(),
  leafState: z.enum(B_LEAF_STATES).nullable(),
  soil: z.enum(B_SOIL_STATES).nullable(),
  lightPosition: z.enum(B_LIGHT_POSITIONS).nullable(),
  careActions: z.array(z.string()),
  adviceSummary: z.string().nullable(),
  createdAt: z.number(),
  aiSnapshot: AiObservationSnapshotSchema.nullable().optional(),
  speciesMismatch: SpeciesMismatchSchema.nullable().optional(),
});
export type TimelineDay = z.infer<typeof TimelineDaySchema>;

export const TimelineResultSchema = z.object({
  plantId: z.string(),
  plantName: z.string(),
  plantSpecies: z.string(),
  totalDays: z.number(),
  days: z.array(TimelineDaySchema),
});
export type TimelineResult = z.infer<typeof TimelineResultSchema>;

/** 6) 阶段报告 */
export const ReportMismatchRecordSchema = z.object({
  recordIndex: z.number(),
  detectedSpecies: z.string(),
  createdAt: z.number(),
});
export type ReportMismatchRecord = z.infer<typeof ReportMismatchRecordSchema>;

export const ReportDataQualitySchema = z.object({
  archiveSpecies: z.string(),
  mismatchCount: z.number(),
  reliableRecordCount: z.number(),
  isMixedArchive: z.boolean(),
  mismatchRecords: z.array(ReportMismatchRecordSchema),
  dataQualityNote: z.string().optional(),
});
export type ReportDataQuality = z.infer<typeof ReportDataQualitySchema>;

export const StageReportRequestSchema = z.object({
  plantId: z.string(),
  /** 省略则自动汇总该植物全部拍照记录 */
  rangeStart: z.number().int().min(1).optional(),
  rangeEnd: z.number().int().min(1).optional(),
  /** 为 true 时跳过指纹去重,强制重新生成 */
  force: z.boolean().optional(),
});
export type StageReportRequest = z.infer<typeof StageReportRequestSchema>;

export const StageReportListItemSchema = z.object({
  reportId: z.string(),
  plantId: z.string(),
  createdAt: z.number(),
  recordCount: z.number(),
  timeStart: z.number().nullable(),
  timeEnd: z.number().nullable(),
  summaryPreview: z.string(),
  mismatchCount: z.number(),
  hasAbnormalEvents: z.boolean(),
  rangeStart: z.number(),
  rangeEnd: z.number(),
});
export type StageReportListItem = z.infer<typeof StageReportListItemSchema>;

export const StageReportListResultSchema = z.object({
  reports: z.array(StageReportListItemSchema),
});
export type StageReportListResult = z.infer<typeof StageReportListResultSchema>;

export const StageReportResultSchema = z.object({
  reportId: z.string(),
  plantId: z.string(),
  plantName: z.string(),
  observationCount: z.number(),
  rangeStart: z.number(),
  rangeEnd: z.number(),
  createdAt: z.number(),
  growthDiary: z.array(z.string()),
  careStats: z.object({
    waterCount: z.number(),
    lightChangeCount: z.number(),
    continueObserveCount: z.number(),
    leafStateChanges: z.array(
      z.object({
        recordIndex: z.number(),
        createdAt: z.number(),
        from: z.string(),
        to: z.string(),
      }),
    ),
    newLeavesCount: z.number(),
  }),
  mismatchCaseCount: z.number(),
  summary: z.string(),
  dataQuality: ReportDataQualitySchema,
  /** 本次请求命中已有报告(相同记录集) */
  duplicate: z.boolean().optional(),
  existingReportId: z.string().optional(),
});
export type StageReportResult = z.infer<typeof StageReportResultSchema>;

// ============================================================
// 旧 schema 保留(兼容现有 web-a/web-b 与 agent-core)
// ============================================================
export const ObserveRequestSchema = z.object({
  photoDataUrl: z.string().optional().nullable(),
  location: z.string().optional().default(''),
  observation: z.string().optional(),
});
export type ObserveRequest = z.infer<typeof ObserveRequestSchema>;

export const ReasonRequestSchema = z.object({
  observation: z.string().min(1, '观察线索不能为空'),
});
export type ReasonRequest = z.infer<typeof ReasonRequestSchema>;

export const EvaluateRequestSchema = z.object({
  action: z.string().min(1, '学生行动不能为空'),
});
export type EvaluateRequest = z.infer<typeof EvaluateRequestSchema>;

export const RecordRequestSchema = z.object({
  sceneId: z.string().optional(),
  observation: z.unknown().optional(),
  reasoning: z.unknown().optional(),
  action: z.string().min(1),
  score: z.number().int().min(0).max(3).optional(),
});
export type RecordRequest = z.infer<typeof RecordRequestSchema>;

export const PlantObserveRequestSchema = z.object({
  plantId: z.string().optional(),
  photoDataUrl: z.string().optional().nullable(),
  soil: z.string().optional(),
  watering: z.string().optional(),
});
export type PlantObserveRequest = z.infer<typeof PlantObserveRequestSchema>;

export const AdviseRequestSchema = z.object({
  observation: z.string().min(1, '观察现象不能为空'),
});
export type AdviseRequest = z.infer<typeof AdviseRequestSchema>;

export const VerifyRequestSchema = z.object({
  day: z.number().int().min(1).max(60),
  note: z.string().min(1),
  photoDataUrl: z.string().optional().nullable(),
});
export type VerifyRequest = z.infer<typeof VerifyRequestSchema>;
